import React, { useEffect, useMemo, useState } from "react";

import Interweave from "interweave";
import { cloneDeep, filter, findIndex, findLastIndex } from "lodash";
import { useToasts } from "react-toast-notifications";

import { CategoriesPanel } from "@pages/sales/tab-table-section/table/customize-table-modal/catgories-panel";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { MetricVisibilityPanel } from "@shared/metric-customization-modal/metric-visibility-panel";
import { Modal } from "@shared/modal";

import {
  type CategoryGroupedConfig,
  createMetricCategories,
  MetricCategory,
  type MetricConfig,
} from "./metric-category-builder";
import { ReorderPanel } from "./reorder-panel";

interface MetricCustomizationModalProps<T extends MetricConfig> {
  metrics: T[];
  onSave: (arg0: T[]) => void;
  onCancel?: () => void;
  title: string;
  subtitle?: string;
}

const DEFAULT_METRIC = "Spend";

export const MetricCustomizationModal = <T extends MetricConfig>({
  metrics,
  onSave,
  onCancel: _onCancel,
  title,
  subtitle,
}: MetricCustomizationModalProps<T>) => {
  const [customizationModalOpen, setCustomizationModalOpen] = useState(false);
  const [configsCopy, setConfigsCopy] = useState<T[]>([]);
  const { addToast } = useToasts();
  const [selectedCategory, setSelectedCategory] = useState<MetricCategory>(
    MetricCategory.NORTHBEAM,
  );

  const showMustHaveOneMetricToast = () => {
    addToast(
      <div>
        <Interweave content="Must have at least one metric." />
      </div>,
      { appearance: "warning", autoDismiss: true },
    );
  };

  useEffect(() => {
    setConfigsCopy(metrics);
  }, [metrics]);

  const visibleMetrics = useMemo(
    () => configsCopy.filter(({ visible }) => visible),
    [configsCopy],
  );

  const toggleVisibility = (metricToToggle: T, visible?: boolean) => {
    if (visibleMetrics.length === 1 && metricToToggle.visible) {
      showMustHaveOneMetricToast();
    } else {
      setConfigsCopy((prevConfigsCopy) => {
        const metricIndex = findIndex(prevConfigsCopy, [
          "id",
          metricToToggle.id,
        ]);
        const [metric] = prevConfigsCopy.splice(metricIndex, 1);
        const lastVisibleMetricIndex = findLastIndex(
          prevConfigsCopy,
          "visible",
        );
        prevConfigsCopy.splice(lastVisibleMetricIndex + 1, 0, {
          ...metric,
          visible: visible ?? !metric.visible,
        });
        return cloneDeep(prevConfigsCopy);
      });
    }
  };

  const toggleAllVisibility = (metrics: T[], visible: boolean) => {
    const metricNames = metrics.map((metric) => metric.name);
    setConfigsCopy((prevConfigsCopy) => {
      const toggledVisibilityConfigs = prevConfigsCopy.map((metric) => {
        if (metricNames.includes(metric.name)) {
          return { ...metric, visible };
        }
        return metric;
      });

      const visibleCount = filter(toggledVisibilityConfigs, "visible");
      if (visibleCount.length === 0) {
        showMustHaveOneMetricToast();
        return toggledVisibilityConfigs.map((metric) => {
          if (metric.name === DEFAULT_METRIC) {
            return { ...metric, visible: true };
          }
          return metric;
        });
      }
      return toggledVisibilityConfigs;
    });
  };

  const reorderVisibleMetrics = (newOrder: T[]) => {
    setConfigsCopy((prevConfigsCopy) => {
      const configsWithoutNewOrder = prevConfigsCopy.filter(({ name }) => {
        return !newOrder.map((metric) => metric.name).includes(name);
      });
      return [...newOrder, ...configsWithoutNewOrder];
    });
  };

  const categories = useMemo<CategoryGroupedConfig<T>>(
    () => createMetricCategories(configsCopy),
    [configsCopy],
  );

  const onCancel = () => {
    _onCancel?.();
    setCustomizationModalOpen(false);
  };

  return (
    <div>
      <SecondaryButton onClick={() => setCustomizationModalOpen(true)}>
        CUSTOMIZE
      </SecondaryButton>
      <Modal
        isOpen={customizationModalOpen}
        onClose={onCancel}
        size="3xl"
        headerTitleProps={{ className: "flex-1" }}
        titleContent={
          <div className="flex flex-col items-center justify-center w-full">
            <h2 className="text-lg font-semibold m-0 mb-1 flex">{title}</h2>
            <p className="text-xs text-zinc-500 font-light m-0">{subtitle}</p>
          </div>
        }
        bodyClassName="h-[calc(100%-80px)] px-0 py-0"
        contentClassName="h-[calc(100%-50px)]"
      >
        <div className="h-full grid grid-rows-[auto_4.5rem] overflow-hidden border-t border-gray-100">
          <div className="h-full grid grid-cols-[14rem_auto_18rem] overflow-hidden relative">
            <CategoriesPanel
              categories={categories}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
            <MetricVisibilityPanel
              visibilityOfMetrics={categories[selectedCategory]}
              selectedCategory={selectedCategory}
              toggleMetricVisibility={toggleVisibility}
              toggleVisibilityOfAllMetrics={toggleAllVisibility}
            />
            <ReorderPanel
              reorderVisibleMetrics={reorderVisibleMetrics}
              visibleMetrics={visibleMetrics}
            />
          </div>
          <div className="border-t border-gray-100 flex justify-end items-center px-6 w-full">
            <SecondaryButton className="mr-3" onClick={onCancel}>
              CANCEL
            </SecondaryButton>
            <PrimaryButton
              onClick={() => {
                onSave(configsCopy);
                setCustomizationModalOpen(false);
              }}
            >
              APPLY
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};
